import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GeographicFilters = ({ 
  selectedCountry = 'all',
  onCountryChange,
  selectedMetric = 'scans',
  onMetricChange,
  selectedTimeRange = 'last7days',
  onTimeRangeChange,
  selectedDeviceType = 'all',
  onDeviceTypeChange,
  countries = [],
  onFiltersChange,
  className = ''
}) => {
  
  // Country options based on actual data
  const getCountryOptions = () => {
    const options = [{ value: 'all', label: 'All Countries', flag: '🌍' }];
    
    countries?.forEach(country => {
      options?.push({
        value: country?.code,
        label: country?.name,
        flag: country?.flag
      });
    });

    return options;
  };

  const metricOptions = [
    { value: 'scans', label: 'Scans', icon: 'BarChart3' },
    { value: 'conversions', label: 'Conversions', icon: 'Target' },
    { value: 'engagement', label: 'Engagement', icon: 'TrendingUp' }
  ];

  const timeRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' }
  ];

  const deviceOptions = [
    { value: 'all', label: 'All Devices', icon: 'Monitor' },
    { value: 'mobile', label: 'Mobile', icon: 'Smartphone' },
    { value: 'tablet', label: 'Tablet', icon: 'Tablet' },
    { value: 'desktop', label: 'Desktop', icon: 'Monitor' }
  ];

  const handleCountryChange = (countryCode) => {
    onCountryChange?.(countryCode);
    onFiltersChange?.();
  };

  const handleMetricChange = (metric) => {
    onMetricChange?.(metric);
    onFiltersChange?.();
  };

  const handleTimeRangeChange = (range) => {
    onTimeRangeChange?.(range);
    onFiltersChange?.();
  };

  const handleDeviceChange = (device) => {
    onDeviceTypeChange?.(device);
    onFiltersChange?.();
  };

  const resetFilters = () => {
    onCountryChange?.('all');
    onMetricChange?.('scans');
    onTimeRangeChange?.('last7days');
    onDeviceTypeChange?.('all');
    onFiltersChange?.();
  };

  const countryOptions = getCountryOptions();

  return (
    <div className={`bg-card rounded-lg border border-border p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Filter" size={16} className="text-primary" />
          <h3 className="font-medium text-foreground">Filters</h3>
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={resetFilters}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Reset All
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Country Filter */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Country
          </label>
          <select
            value={selectedCountry}
            onChange={(e) => handleCountryChange(e?.target?.value)}
            className="w-full p-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {countryOptions?.map((country) => (
              <option key={country?.value} value={country?.value}>
                {country?.flag} {country?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Metric Filter */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Metric
          </label>
          <select
            value={selectedMetric}
            onChange={(e) => handleMetricChange(e?.target?.value)}
            className="w-full p-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {metricOptions?.map((metric) => (
              <option key={metric?.value} value={metric?.value}>
                {metric?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Time Range Filter */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Time Range
          </label>
          <select
            value={selectedTimeRange}
            onChange={(e) => handleTimeRangeChange(e?.target?.value)}
            className="w-full p-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {timeRangeOptions?.map((range) => (
              <option key={range?.value} value={range?.value}>
                {range?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Device Filter */}
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-2 block">
            Device Type
          </label>
          <select
            value={selectedDeviceType}
            onChange={(e) => handleDeviceChange(e?.target?.value)}
            className="w-full p-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {deviceOptions?.map((device) => (
              <option key={device?.value} value={device?.value}>
                {device?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Active Filters Summary */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name="Filter" size={12} />
            <span>Active Filters:</span>
            <div className="flex items-center space-x-1">
              {selectedCountry !== 'all' && (
                <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs">
                  {countryOptions?.find(c => c?.value === selectedCountry)?.label}
                </span>
              )}
              {selectedMetric !== 'scans' && (
                <span className="bg-secondary/10 text-secondary px-2 py-1 rounded text-xs">
                  {metricOptions?.find(m => m?.value === selectedMetric)?.label}
                </span>
              )}
              {selectedTimeRange !== 'last7days' && (
                <span className="bg-accent/10 text-accent px-2 py-1 rounded text-xs">
                  {timeRangeOptions?.find(t => t?.value === selectedTimeRange)?.label}
                </span>
              )}
              {selectedDeviceType !== 'all' && (
                <span className="bg-muted text-muted-foreground px-2 py-1 rounded text-xs">
                  {deviceOptions?.find(d => d?.value === selectedDeviceType)?.label}
                </span>
              )}
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground">
            {countries?.length || 0} results
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeographicFilters;